'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { WALLET_AUTOCONNECT_DELAY_MS } from 'src/hooks/wallets/consts';
import { VelocityWindow } from 'src/window/velocityWindow';
import { getIsInEmulationMode } from 'src/utils/routerUtils';
import { useAppInitialLoadContext } from 'src/providers/app/AppInitialLoadProvider/AppInitialLoadProvider';

/**
 * Should skip autoconnect if we detect emulation mode params in the URL : Otherwise it tries to connect to the regular current wallet then switches to the emulation wallet afterwards which is unnecessary
 * @returns
 */
const useShouldSkipAutoConnect = () => {
	const searchParams = useSearchParams();
	const isInEmulationMode = getIsInEmulationMode(searchParams);

	// Put this in a ref because it should only ever hold the initial value :: The emulation params might change later but the autoconnect value should not change
	return useRef(isInEmulationMode);
};

/**
 * 🚨 Currently only works for solana/wallet-adapater wallets. Not our custom integrations.
 *
 * Phantom mobile app has a weird bug where autoconnect breaks if we try to programmatically enable/disable it based on whether they've used the UI before. need to use our own custom autoconnect logic to get around this.
 *
 * The logic is as follows:
 * - We want to wait some hardcoded timeout before we try to connect
 * - We only want to autoconnect if we have connected a wallet before
 * - We also only want to autoconnect if we have the name of the previous wallet saved in local state
 * - If all of these things are true, then after the timeout try to connect to that previous wallet. Only do this once by tracking if we've already tried to autoconnect aswelll
 */
export const useCustomWalletAutoconnect = () => {
	const { matchingWallet, shouldAttemptWalletAdapterAutoconnect } =
		useAppInitialLoadContext();
	const alreadyTriedToAutoconnect = useRef(false);
	const [hasAutoconnectDelayElapsed, setHasAutoconnectDelayElapsed] =
		useState(false);

	const skipAutoConnectRef = useShouldSkipAutoConnect();

	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setHasAutoconnectDelayElapsed(true);
		}, WALLET_AUTOCONNECT_DELAY_MS);

		return () => {
			clearTimeout(timeoutId);
		};
	}, []);

	// ⭐️ THIS HOOK IS WHAT MAKES THE UI AUTOCONNECT TO THE WALLET
	useEffect(() => {
		if (skipAutoConnectRef.current) {
			return;
		}

		if (!hasAutoconnectDelayElapsed) {
			return;
		}

		// The purpose of this is to prevent trying to connect to a wallet multiple times if the wallet context changes for whatever reason (and it previous only tried when it SHOULD HAVE SUCCEEDED so we don't expect a change).
		if (alreadyTriedToAutoconnect.current) return;

		if (!shouldAttemptWalletAdapterAutoconnect || !matchingWallet) {
			return;
		}

		VelocityWindow.shouldTrackAutoConnectStartupTimeMetrics.set(true); // We're going to try to autoconnect, so we want to enable tracking of related metrics

		alreadyTriedToAutoconnect.current = true;
		matchingWallet.adapter.connect();
	}, [
		hasAutoconnectDelayElapsed,
		matchingWallet,
		shouldAttemptWalletAdapterAutoconnect,
	]);
};
